import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {FormControl, NgForm} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import BasePtpForm from "../base-ptp-form";
import Utils from "src/utils";
import {PerFormaAPIService} from "src/app/services/pfapi.service";

@Component({
    selector: "app-po",
    templateUrl: "./po.component.html",
    styleUrls: ["./po.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PoComponent extends BasePtpForm implements OnInit {
    constructor(
        api: PerFormaAPIService,
        private route: ActivatedRoute,
        protected modal: NgbModal
    ) {
        super(api, modal);
    }

    ModalOptions = {animation: false, backdropClass: "backdrop"};
    PO: any = {ContactInformation: {}};
    NewRequest: any = {};
    POID;
    // display-only, formatted dates (the model keeps its raw ISO values for round-tripping)
    CreateDateDisplay = "";
    PrCreateDateDisplay = "";
    HasLocationError = true;
    HasVendorNameError = true;
    HasVendorNumberError = true;
    Departments = [];
    ShippingLocations = [];
    Locations = [];
    Submitted = false;
    ShippingLocation;

    // used to store the most recently submitted note
    NoteField = new FormControl();

    Refresh() {
        this.getData();
    }

    CloseWindow = event => {
        window.parent.close();
    };

    getData = () => {
        this.api.getApiPTPGetPO({poId: this.POID}).subscribe({
            next: (data: any) => {
                // Set scope purchase order.
                this.PO = data;
                // format dates for display only — leave the model's raw ISO values untouched so the PO
                // round-trips correctly on update (the formatted string is not a valid DateTime to post back)
                this.CreateDateDisplay = Utils.FormatDate(new Date(this.PO.CreateDate + "Z"));
                if (this.PO.PurchaseRequest !== null) {
                    this.PrCreateDateDisplay = Utils.FormatDate(new Date(this.PO.PurchaseRequest.CreateDate + "Z"));
                }

                this.HasLocationError = true;
                this.HasVendorNameError = true;
                this.HasVendorNumberError = true;

                if (this.PO.Location > 0) {
                    this.HasLocationError = false;
                }

                const vendorName = this.PO.VendorName.trim().toLowerCase();
                const vendorNumber = this.PO.VendorNumber.trim();

                if (vendorName != "" && vendorName != "unspecified") {
                    this.HasVendorNameError = false;
                }

                if (vendorNumber != "") {
                    this.HasVendorNumberError = false;
                }

                this.SelectPOLocation();

                // Set loading as false.
                this.Loading = false;

                // see if we need to scroll
                if (this.DoScroll && this.ScrollTo?.length > 0) {
                    setTimeout(() => {
                        this.ScrollToElement(this.ScrollTo);
                    }, 500);
                }
            },
            error: error => {
                this.Error(error.error);
                this.Loading = false;
            }
        });
    };

    ngOnInit(): void {
        window.addEventListener("message", event => {
            if (event?.data?.action === "SaveForm") {
                this.Update(() => {
                    let errors = false;

                    // Show notification
                    if (this.HasLocationError) {
                        this.Error("Updated: Missing Location");
                        errors = true;
                    }

                    if (this.HasVendorNameError) {
                        this.Error("Updated: Missing Vendor Name");
                        errors = true;
                    }

                    if (this.HasVendorNumberError) {
                        this.Error("Updated: Missing Vendor Number");
                        errors = true;
                    }

                    if (errors) {
                        this.Loading = false;
                        return;
                    }

                    window.parent.postMessage({...event.data, action: "LoadForm"});
                });
            }
        });

        // get the query params
        const params = new URL(window.location.toString()).searchParams;

        const show = params.get("show") ?? "";

        if (show != "") {
            this.DoScroll = true;
            this.ScrollTo = show;
        }

        this.api.getApiPTPGetDeptsAndLocations().subscribe(data => {
            this.Locations = data.Locations;
            this.ShippingLocations = data.Locations;
            this.Departments = data.Departments;
            this.route.queryParams.subscribe(f => {
                if (f.poid) {
                    this.POID = f.poid;
                    this.getData();
                }
            });
        });
    }

    getErrorStatus = () => {
        if (this.HasLocationError || this.HasVendorNameError || this.HasVendorNumberError) {
            return "text-danger has-error";
        }
    };

    SelectPOLocation = () => {
        this.HasLocationError = this.PO.Location === undefined || this.PO.Location < 1;
    };

    ValidateVendorName = () => {
        const vendorName = this.PO.VendorName.trim().toLowerCase();
        this.HasVendorNameError = vendorName == "" || vendorName == "unspecified";
    };

    ValidateVendorNumber = () => {
        this.HasVendorNumberError = this.PO.VendorNumber.trim() == "";
    };

    // Called when the shipping location dropdown is changed.
    SelectLocation = () => {
        if (!this.ShippingLocation) {
            if (!this.PO.ContactInformation) {
                this.PO.ContactInformation = {};
            }
            this.PO.ContactInformation.ShipToAddressLine1 = "";
            this.PO.ContactInformation.ShipToAddressLine2 = "";
            this.PO.ContactInformation.ShipToCity = "";
            this.PO.ContactInformation.ShipToState = "";
            this.PO.ContactInformation.ShipToZip = "";
            this.PO.ContactInformation.ShipToCountry = "";
            this.PO.ContactInformation.ShipToEmail = "";
            this.PO.ContactInformation.ShipToFax = "";
            this.PO.ContactInformation.ShipToPhone = "";
            return;
        }

        if (this.ShippingLocations && this.ShippingLocations.length > 0) {
            let foundShipping = false;

            for (let i = 0; i < this.ShippingLocations.length; i++) {
                const currentShip = this.ShippingLocations[i];
                if (currentShip.PTPShippingLocationID === this.ShippingLocation.PTPShippingLocationID) {
                    if (this.PO.ContactInformation === null) {
                        this.PO.ContactInformation = {};
                    }
                    this.PO.ContactInformation.ShipToAddressLine1 = currentShip.ShipToAddressLine1;
                    this.PO.ContactInformation.ShipToAddressLine2 = currentShip.ShipToAddressLine2;
                    this.PO.ContactInformation.ShipToCity = currentShip.ShipToCity;
                    this.PO.ContactInformation.ShipToState = currentShip.ShipToState;
                    this.PO.ContactInformation.ShipToZip = currentShip.ShipToZip;
                    this.PO.ContactInformation.ShipToCountry = currentShip.ShipToCountry;
                    this.PO.ContactInformation.ShipToEmail = currentShip.ShipToEmail;
                    this.PO.ContactInformation.ShipToFax = currentShip.ShipToFax;
                    this.PO.ContactInformation.ShipToPhone = currentShip.ShipToPhone;

                    this.HasLocationError = false;
                    foundShipping = true;
                }
            }

            if (foundShipping === false) {
                this.HasLocationError = true;
            }
        }
    };

    // Updates the PO request with the scope model.
    Update = (next?: () => void) => {
        this.Submitted = true;

        const vendorName = this.PO.VendorName.trim().toLowerCase();
        const vendorNumber = this.PO.VendorNumber.trim();
        let errors = false;

        if (vendorName == "" || vendorName == "unspecified") {
            this.HasVendorNameError = true;
            this.Error("Missing Vendor Name");
            errors = true;
        }

        if (vendorNumber == "") {
            this.HasVendorNumberError = true;
            this.Error("Missing Vendor Number");
            errors = true;
        }

        if (!this.PO.Location || this.PO.Location === 0) {
            this.Error("Missing Location");
            this.HasLocationError = true;
            errors = true;
        }

        if (this.PO.LineItems.some(i => i.Description == "")) {
            this.Error("Missing Line Item Description");
            errors = true;
        }

        if (errors) return;

        this.Loading = true;

        this.api.postApiPTPUpdatePO(this.PO).subscribe(
            next ??
                (() => {
                    // Reload data.
                    this.getData();

                    // Show notification
                    this.Success("Updated successfully");
                })
        );
    };

    ShowCreateWindow = tmpl => {
        this.modal.open(tmpl, this.ModalOptions);
        window.parent.scrollTo({top: 0, behavior: "smooth"});
    };

    // Removes an item from the list of line items.
    RemoveItem = item => {
        for (let i = this.PO.LineItems.length; i--; ) {
            if (this.PO.LineItems[i] === item) {
                this.PO.LineItems.splice(i, 1);
                break;
            }
        }
    };

    // Adds an item to the list of line items.
    AddItem = (form: NgForm, item) => {
        if (!form.valid) return;
        this.PO.LineItems.push({
            ID: 0,
            Description: item.Description,
            Quantity: item.Quantity,
            Price: item.Price
        });

        item.Description = "";
        item.Quantity = "";
        item.Price = "";

        this.modal.dismissAll();

        this.api.postApiPTPUpdatePO(this.PO).subscribe(() => {
            // Reload data.
            this.getData();

            // Show notification
            if (this.HasLocationError) {
                this.Error("Updated: Missing Location");
            }

            if (this.HasVendorNameError) {
                this.Error("Updated: Missing Vendor Name");
            }

            if (this.HasVendorNumberError) {
                this.Error("Updated: Missing Vendor Number");
            }
        });
    };

    // Gets the total of all line items.
    GetLinesItemTotal = () => {
        let total = 0;
        if (this.PO && this.PO.LineItems && this.PO.LineItems.length > 0) {
            for (let i = 0; i < this.PO.LineItems.length; i++) {
                const item = this.PO.LineItems[i];
                total += item.Quantity * item.Price;
            }
        }
        return total.toFixed(2);
    };

    addNote = () => {
        const note = {
            ID: 0,
            UserName: "you",
            Note: this.NoteField.value,
            NoteTime: new Date().toJSON()
        };

        this.PO.Notes = [note, ...this.PO.Notes];

        this.NoteField.setValue("");

        //close the modal
        this.modal.dismissAll();
        // Set loading as false.
        this.Loading = false;
    };

    // Removes an note from the list of notes.
    RemoveNote = note => {
        for (let i = this.PO.Notes.length; i--; ) {
            if (this.PO.Notes[i] == note) {
                this.PO.Notes.splice(i, 1);
                break;
            }
        }
    };
}
